import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { IMAGE_EDITOR_MODES } from "store/imageEditor";
import { useTheme } from "@emotion/react";
import { fonts } from "fonts";
import { imageEditorActions } from "store/imageEditor";

const allowedSizes = [
  { width: 1024, height: 1024, str: "square" },
  { width: 1024, height: 1536, str: "portrait" },
  { width: 1536, height: 1024, str: "landscape" },
];

const ImageMaskEditor = forwardRef(({}, ref) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const imageCanvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const containerRef = useRef(null);

  const [painting, setPainting] = useState(false);
  const [scale, setScale] = useState(1);
  const [rectStart, setRectStart] = useState(null);

  const imageEditorState = useSelector((state) => state.imageEditor);
  const image64 = imageEditorState.image;
  const mask64 = imageEditorState.mask;
  const mode = useSelector((state) => state.imageEditor.mode);
  const brushSize = useSelector((state) => state.imageEditor.brushSize);

  // --- Helper to normalize mouse/touch events ---
  const getEventPos = (e) => {
    const rect = e.target.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) / scale,
      y: (clientY - rect.top) / scale,
    };
  };

  const handleUrlImage = (url) => {
    console.log(url);
    if (!url) return;
    const img = new Image();
    img.crossOrigin = "anonymous"; // allow CORS if the image server permits
    img.onload = () => {
      const padded = padImageToClosestRatio(img);
      padded.onload = () => {
        dispatch(imageEditorActions.setImage(padded.src));
      };
    };
    img.onerror = () => {
      console.error("Failed to load image from URL:", url);
    };
    img.src = url;
  };

  // --- Load image from file ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const padded = padImageToClosestRatio(img);
      padded.onload = () => {
        dispatch(imageEditorActions.setImage(padded.src));
      };
    };
    img.src = URL.createObjectURL(file);
  };

  const padImageToClosestRatio = (img) => {
    const imgRatio = img.width / img.height;
    let closest = allowedSizes[0];
    let minDiff = Infinity;
    for (const s of allowedSizes) {
      const r = s.width / s.height;
      const diff = Math.abs(imgRatio - r);
      if (diff < minDiff) {
        minDiff = diff;
        closest = s;
      }
    }

    console.log(closest);
    dispatch(imageEditorActions.setRatio(closest.str));

    const canvas = document.createElement("canvas");
    canvas.width = closest.width;
    canvas.height = closest.height;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scale = Math.min(
      canvas.width / img.width,
      canvas.height / img.height
    );
    const w = img.width * scale;
    const h = img.height * scale;
    const x = (canvas.width - w) / 2;
    const y = (canvas.height - h) / 2;
    ctx.drawImage(img, x, y, w, h);

    const paddedImg = new Image();
    paddedImg.src = canvas.toDataURL("image/png");
    return paddedImg;
  };

  // --- Restore from Redux state ---
  useEffect(() => {
    if (image64 && imageCanvasRef.current) {
      const img = new Image();
      img.src = image64;
      img.onload = () => {
        const containerWidth = containerRef.current.offsetWidth;
        const scaleFactor = containerWidth / img.width;
        setScale(scaleFactor);

        const canvas = imageCanvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;

        const maskCanvas = maskCanvasRef.current;
        maskCanvas.width = img.width;
        maskCanvas.height = img.height;

        const previewCanvas = previewCanvasRef.current;
        previewCanvas.width = img.width;
        previewCanvas.height = img.height;

        ctx.drawImage(img, 0, 0);
      };
    }
  }, [image64]);

  useEffect(() => {
    if (!maskCanvasRef.current) return;
  
    const ctx = maskCanvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
  
    if (mask64) {
      const maskImg = new Image();
      maskImg.src = mask64;
      maskImg.onload = () => {
        ctx.drawImage(maskImg, 0, 0);
      };
    }
  }, [mask64]);

  const downloadMask = () => {
    const mask = exportMask();
    if (!mask) return;

    const link = document.createElement("a");
    link.href = mask;
    link.download = "mask.png";
    link.click();
  };

  // --- Painting / Rectangle logic ---
  const startPaint = (e) => {
    if (mode === IMAGE_EDITOR_MODES.rect) {
      setRectStart(getEventPos(e));
    } else {
      setPainting(true);
      draw(e, true);
    }
  };

  const draw = (e, isClick = false) => {
    if (mode === IMAGE_EDITOR_MODES.rect) {
      if (!rectStart) return;
      const { x, y } = getEventPos(e);

      const previewCtx = previewCanvasRef.current.getContext("2d");
      previewCtx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);

      previewCtx.fillStyle = "rgba(255,255,255,0.3)";
      previewCtx.fillRect(rectStart.x, rectStart.y, x - rectStart.x, y - rectStart.y);

      previewCtx.strokeStyle = "rgba(0,255,0,0.8)";
      previewCtx.lineWidth = 2;
      previewCtx.strokeRect(rectStart.x, rectStart.y, x - rectStart.x, y - rectStart.y);
      return;
    }

    if (!painting && !isClick) return;

    const canvas = maskCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getEventPos(e);

    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";

    if (mode === IMAGE_EDITOR_MODES.paint) {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = "rgba(255,255,255,1)";
    } else {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
    }

    if (isClick) {
      ctx.beginPath();
      ctx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
      ctx.fillStyle = mode === IMAGE_EDITOR_MODES.paint ? "white" : "black";
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const endPaint = (e) => {
    if (mode === IMAGE_EDITOR_MODES.rect && rectStart) {
      const { x, y } = getEventPos(e);
      const ctx = maskCanvasRef.current.getContext("2d");

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "white";
      ctx.fillRect(rectStart.x, rectStart.y, x - rectStart.x, y - rectStart.y);

      setRectStart(null);
      previewCanvasRef.current
        .getContext("2d")
        .clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);

      dispatch(imageEditorActions.setMask(maskCanvasRef.current.toDataURL("image/png")));
      return;
    }

    setPainting(false);
    const ctx = maskCanvasRef.current.getContext("2d");
    ctx.beginPath();
    if (maskCanvasRef.current) {
      const mask64 = maskCanvasRef.current.toDataURL("image/png");
      dispatch(imageEditorActions.setMask(mask64));
    }
  };

  // --- Brush preview (for paint/erase only) ---
  const showBrushPreview = (e) => {
    if (!previewCanvasRef.current || e.touches) return;
    const previewCtx = previewCanvasRef.current.getContext("2d");
    const { x, y } = getEventPos(e);
  
    // Clear previous preview
    previewCtx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
  
    if (mode === IMAGE_EDITOR_MODES.rect) {
      // Draw + cursor
      previewCtx.strokeStyle = "rgba(0,255,0,0.8)";
      previewCtx.lineWidth = 2;
      const size = 10;
      previewCtx.beginPath();
      previewCtx.moveTo(x - size, y);
      previewCtx.lineTo(x + size, y);
      previewCtx.moveTo(x, y - size);
      previewCtx.lineTo(x, y + size);
      previewCtx.stroke();
  
      // Draw rectangle if dragging
      if (rectStart) {
        const rectX = rectStart.x;
        const rectY = rectStart.y;
        const rectW = x - rectStart.x;
        const rectH = y - rectStart.y;
  
        previewCtx.fillStyle = "rgba(255,255,255,0.3)"; // semi-transparent fill
        previewCtx.fillRect(rectX, rectY, rectW, rectH);
  
        previewCtx.strokeStyle = "rgba(0,255,0,0.8)"; // rectangle border
        previewCtx.lineWidth = 2;
        previewCtx.strokeRect(rectX, rectY, rectW, rectH);
      }
    } else {
      // Existing brush preview
      previewCtx.beginPath();
      previewCtx.arc(x, y, brushSize / 2, 0, 2 * Math.PI);
      previewCtx.strokeStyle =
        mode === IMAGE_EDITOR_MODES.paint ? "rgba(0,255,0,0.8)" : "rgba(255,0,0,0.8)";
      previewCtx.lineWidth = 2;
      previewCtx.stroke();
    }
  };
  
  
  const hideBrushPreview = () => {
    if (!previewCanvasRef.current) return;
    const ctx = previewCanvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
  };
  

  // --- Exports ---
  const exportMask = () => {
    if (!maskCanvasRef.current) return null;
    return maskCanvasRef.current.toDataURL("image/png");
  };

  const clearMask = () => {
    if (!maskCanvasRef.current) return;
    const ctx = maskCanvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
    dispatch(imageEditorActions.setMask(null));
  };

  const exportImage = () => {
    if (!image64) return null;
    return image64;
  };

  useImperativeHandle(ref, () => ({
    exportMask,
    exportImage,
    clearMask,
    downloadMask,
    handleUrlImage,
  }));

  return (
    <Stack spacing={2} alignItems="center" py={2} width="100%">
      <Button variant="outlined" component="label">
        {image64 ? "Select another image" : "Select image"}
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
      </Button>

      {!image64 && (
        <Stack alignItems="center" justifyContent="center">
          <Typography fontFamily={fonts.anton} sx={{ color: theme.palette.text.main }}>
            No image selected
          </Typography>
        </Stack>
      )}

      {image64 && (
        <Box ref={containerRef} sx={{ position: "relative", width: "100%", maxWidth: "800px", overflow: "hidden" }}>
          {/* Base image */}
          <canvas
            ref={imageCanvasRef}
            style={{ display: "block", width: `100%` }}
          />
          {/* Mask overlay */}
          <canvas
            ref={maskCanvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `100%`,
              cursor: "none",
              opacity: 0.5,
            }}
            onMouseDown={startPaint}
            onMouseUp={endPaint}
            onMouseMove={(e) => {
              draw(e);
              showBrushPreview(e);
            }}
            onMouseLeave={hideBrushPreview}
            onTouchStart={(e) => {
              e.preventDefault();
              startPaint(e);
            }}
            onTouchMove={(e) => {
              if (painting || mode === IMAGE_EDITOR_MODES.rect) {
                e.preventDefault();
                draw(e);
              }
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              endPaint(e);
            }}
          />
          {/* Preview overlay */}
          <canvas
            ref={previewCanvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              pointerEvents: "none",
              border: "2px solid white",
              boxSizing: "border-box",
            }}
          />
        </Box>
      )}
    </Stack>
  );
});

export default ImageMaskEditor;
